'use client';

import { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Slide } from '@mui/material';
import { useResolvedColorScheme } from '@/hooks/useResolvedColorScheme';
import { AppBar, Box, Drawer, IconButton, Stack } from '@/primitives';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { NAV_HEIGHT } from '@/configs/layout';
import { PUBLIC_ROUTES } from '@/configs/routes';
import { useIsPublicSearch } from '@/hooks/useIsPublicSearch';
import SocietySearchTabMobileSelect from '@/components/publicSearch/searchPage/SocietySearchTabMobileSelect';
import SocietySearchHeaderLogo from '@/components/publicSearch/searchPage/SocietySearchHeaderLogo';
import Navigation from './Navigation';

const HEADER_TRANSITION_MS = 280;

export default function MobileToolbar() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isSocietySearch = useIsPublicSearch();
  const { resolvedScheme } = useResolvedColorScheme();
  const breedlogicLogoSrc =
    resolvedScheme === 'dark'
      ? '/breedlogic-logo-dark.svg'
      : '/breedlogic-logo.svg';

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          columnGap: 1,
          px: '12px',
          py: '8px',
          borderBottom: '3px solid',
          borderBottomColor: 'divider',
          height: `${NAV_HEIGHT}px`,
          backgroundColor: 'background.paper',
          boxShadow: 'none',

          '@media print': {
            display: 'none',
          },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          gap={1}
          sx={{ flexShrink: 0 }}
        >
          <Link
            href={PUBLIC_ROUTES.HOME}
            style={{
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
              width: 112,
              height: 36,
            }}
          >
            <Image
              alt="BreedLOGIC Logo"
              height={37}
              width={176}
              src={breedlogicLogoSrc}
              style={{ objectFit: 'contain', width: '100%', height: '100%' }}
            />
          </Link>
          {isSocietySearch ? <SocietySearchHeaderLogo /> : null}
        </Stack>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            overflow: 'hidden',
          }}
        >
          <Slide
            appear
            direction={isSocietySearch ? 'down' : 'up'}
            timeout={HEADER_TRANSITION_MS}
            in
            key={
              isSocietySearch
                ? 'mobile-bar-center-society'
                : 'mobile-bar-center-default'
            }
          >
            <Box
              sx={{
                width: '100%',
                minWidth: 0,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {isSocietySearch ? (
                <SocietySearchTabMobileSelect />
              ) : (
                <Box sx={{ flex: 1 }} />
              )}
            </Box>
          </Slide>
        </Box>
        <Stack
          direction="row"
          alignItems="center"
          sx={{ flexShrink: 0, ml: 'auto' }}
        >
          <IconButton size="small" onClick={() => setDrawerOpen(true)}>
            <MenuIcon sx={{ color: 'text.primary', fontSize: 24 }} />
          </IconButton>
        </Stack>
      </AppBar>
      <Drawer
        anchor="left"
        open={drawerOpen}
        sx={{
          flexShrink: 0,

          '& .MuiDrawer-paper': {
            p: 0,
            width: '90%',
            maxWidth: '320px',
            boxSizing: 'border-box',
            borderRadius: 0,
            padding: '12px 10px 10px',
          },

          '@media print': {
            display: 'none',
          },
        }}
        onClose={() => setDrawerOpen(false)}
      >
        <IconButton
          size="small"
          sx={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            zIndex: 1,
          }}
          onClick={() => setDrawerOpen(false)}
        >
          <CloseIcon sx={{ color: 'text.secondary', fontSize: 22 }} />
        </IconButton>
        <Box sx={{ pt: '38px', width: '100%' }}>
          <Navigation onNavigate={() => setDrawerOpen(false)} />
        </Box>
      </Drawer>
    </>
  );
}
